import { Box } from '@mui/material';
import DatePicker from 'components/date-picker';
import LoadingSpinner from 'components/loading-spinner';
import NumberField from 'components/number-field';
import TextField from 'components/text-field';
import { useErrorHandlerContextState } from 'contexts/error-handler/ErrorHandlerContext';
import dayjs from 'dayjs';
import { Control, UseFormGetValues, UseFormTrigger, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useScenarioState } from 'views/bluviu-nutrition/features/scenarios/context/scenario-context';
import { UpdateScenarioRequest } from 'views/bluviu-nutrition/features/scenarios/types/update-scenario-input.type';
import isScenarioDisabled from 'views/bluviu-nutrition/features/scenarios/utils/validate-scenario';

type Props = {
  control: Control<UpdateScenarioRequest, any>;
  isLoading: boolean;
  getValues: UseFormGetValues<UpdateScenarioRequest>;
  trigger: UseFormTrigger<UpdateScenarioRequest>;
};

const PAGE_NAME_ID = 'bluViuNutrition.setUp';

const ScenarioInputs: React.FC<Props> = ({ control, isLoading, getValues, trigger }) => {
  const intl = useIntl();
  const {
    scenarioQuery: { data: scenario, isLoading: isScenarioLoading },
  } = useScenarioState();

  const watch = useWatch({
    control: control,
  });

  const { errors } = useErrorHandlerContextState();

  if (isScenarioLoading) return <LoadingSpinner />;
  if (!scenario) return;

  return (
    <Box display={'flex'} flexDirection={'row'} gap={4}>
      <DatePicker
        dark
        fullWidth
        disabled={isScenarioDisabled(scenario, isLoading, true)}
        label={intl.formatMessage({ id: `${PAGE_NAME_ID}.settingsRun.startDate` })}
        maxDate={watch?.endDate}
        error={!!watch?.startDate && !!watch?.endDate && !dayjs(watch.startDate).isBefore(dayjs(watch.endDate))}
        helperText={
          !!watch?.startDate && !dayjs(watch.endDate).isAfter(dayjs(watch.startDate))
            ? intl.formatMessage({ id: `${PAGE_NAME_ID}.settingsRun.startDateError` })
            : ''
        }
        controller={{
          name: 'startDate',
          control: control,
          rules: {
            required: intl.formatMessage({ id: 'form.rules.required' }),
            validate: {
              beforeEndDate: (v) =>
                dayjs(v).isBefore(dayjs(getValues('endDate')).startOf('day'))
                  ? true
                  : intl.formatMessage({ id: `${PAGE_NAME_ID}.settingsRun.startDateError` }),
            },
          },
        }}
      />
      <DatePicker
        dark
        fullWidth
        error={!!watch?.endDate && !!watch?.startDate && !dayjs(watch.endDate).isAfter(dayjs(watch.startDate))}
        helperText={
          !!watch?.startDate && !dayjs(watch.endDate).isAfter(dayjs(watch.startDate))
            ? intl.formatMessage({ id: `${PAGE_NAME_ID}.settingsRun.endDateError` })
            : ''
        }
        disabled={isScenarioDisabled(scenario, isLoading, true)}
        label={intl.formatMessage({ id: `${PAGE_NAME_ID}.settingsRun.endDate` })}
        minDate={watch?.startDate}
        maxDate={dayjs().add(40, 'year')}
        controller={{
          name: 'endDate',
          control: control,
          rules: {
            required: intl.formatMessage({ id: 'form.rules.required' }),
            validate: {
              afterStartDate: (v) =>
                dayjs(v).isAfter(dayjs(getValues('startDate')).startOf('day')) ??
                intl.formatMessage({ id: `${PAGE_NAME_ID}.settingsRun.endDateError` }),
            },
          },
        }}
      />
      <NumberField
        dark
        fullWidth
        label={intl.formatMessage({ id: `${PAGE_NAME_ID}.settingsRun.fishWeight` })}
        disabled={isScenarioDisabled(scenario, isLoading, true)}
        error={!!watch.fishWeight && !(parseInt(watch.fishWeight) < 50 || parseInt(watch.fishWeight) > 3000)}
        helperText={
          !!watch.fishWeight && (parseInt(watch.fishWeight) < 50 || parseInt(watch.fishWeight) >= 3000) ?
            intl.formatMessage({ id: `${PAGE_NAME_ID}.warningText.range` }) : ''
        }
        controller={{
          name: 'fishWeight',
          control: control,
          rules: {
            required: intl.formatMessage({ id: 'form.rules.required' }),
            validate: {
              positive: (v) => (parseInt(v) - 1 ? true : false),
              between: (value: number) =>
                value >= 50 && value <= 3000
                  ? true
                  : intl.formatMessage({ id: `${PAGE_NAME_ID}.warningText.range` }),
            },
          },
        }}
        endAdornment={'g'}
      />
    </Box>
  );
};

export default ScenarioInputs;
